(function () {
  // 读取 Liquid 注入的全局埋点配置，避免在 JS 中硬编码店铺信息。
  const config = window.__trackingConfig || {};
  const customer = config.customer || {};
  const shop = config.shop || {};
  // 保存每个商品最近一次选项状态，用于变体切换差异判断。
  const interactionTimers = new Map();
  // 保存颜色/尺码切换计数（按商品维度）。
  const variantSwitchCounters = new Map();
  // 最近事件去重缓存：短时间重复触发不重复上报。
  const recentEventCache = new Map();
  // 去重时间窗（毫秒）。
  const RECENT_EVENT_TTL_MS = 500;

  // 安全解析 JSON，失败返回 fallback，防止异常中断埋点流程。
  function safeJsonParse(str, fallback) {
    try {
      return JSON.parse(str);
    } catch (e) {
      return fallback;
    }
  }

  // 生成标准 ISO 时间字符串，作为事件发生时间。
  function nowIso() {
    return new Date().toISOString();
  }

  // 生成事件唯一 ID，用于去重和问题追踪。
  function generateEventId() {
    return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }

  // 通过 body 上 template-* class 推断页面类型。
  function getPageType() {
    const body = document.body;
    if (!body) return 'other';
    const classes = Array.from(body.classList);
    const tpl = classes.find((cls) => cls.indexOf('template-') === 0);
    if (!tpl) return 'other';
    const raw = tpl.replace('template-', '');
    if (raw === 'index') return 'home';
    if (raw.indexOf('product') === 0) return 'product';
    if (raw.indexOf('collection') === 0) return 'collection';
    if (raw.indexOf('cart') === 0) return 'cart';
    if (raw.indexOf('search') === 0) return 'search';
    return raw || 'other';
  }

  // 所有事件共享的基础字段（统一口径）。
  function basePayload() {
    return {
      event_id: generateEventId(),
      event_time: nowIso(),
      page_type: getPageType(),
      shopify_customer_id: customer.id || null,
      customer_email: config.send_pii_to_ga4 ? customer.email || null : null,
      customer_phone: config.send_pii_to_ga4 ? customer.phone || null : null,
      currency: shop.currency || null,
      send_pii_to_ga4: !!config.send_pii_to_ga4
    };
  }

  // 清理去重缓存中过期项，防止缓存无限增长。
  function cleanupRecentEvents() {
    const cutoff = Date.now() - RECENT_EVENT_TTL_MS;
    recentEventCache.forEach((value, key) => {
      if (value < cutoff) recentEventCache.delete(key);
    });
  }

  // 构造事件去重 key，选择能表达“同一行为”的关键字段。
  function getDedupKey(eventName, payload) {
    const identityParts = [
      eventName,
      payload.page_type || '',
      payload.product_id || '',
      payload.variant_id || '',
      payload.option_value || '',
      payload.content_title || '',
      payload.link_href || '',
      payload.interaction_source || '',
      payload.reason_bucket_hint || ''
    ];
    return identityParts.join('|');
  }

  // 统一埋点发送入口：合并参数 -> 去重 -> push 到 dataLayer。
  function pushTrackingEvent(eventName, payload) {
    const mergedPayload = Object.assign({}, basePayload(), payload || {}, { event: eventName });
    cleanupRecentEvents();
    const dedupKey = getDedupKey(eventName, mergedPayload);
    if (recentEventCache.has(dedupKey)) return;
    recentEventCache.set(dedupKey, Date.now());
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(mergedPayload);
  }

  // 从当前节点就近查找 product-data，找不到再回退全局节点。
  function getProductDataElement(node) {
    if (node && node.closest) {
      const local = node.closest('.js-product') || node.closest('product-form');
      if (local) {
        const localData = local.querySelector('#product-data');
        if (localData) return localData;
      }
    }
    return document.getElementById('product-data');
  }

  // 提取当前商品上下文：product_id / title / variant_id / quantity。
  function getCurrentProductContext(node) {
    const productDataEl = getProductDataElement(node);
    let product = null;
    if (productDataEl && productDataEl.dataset.product) {
      product = safeJsonParse(productDataEl.dataset.product, null);
    }

    const form = node && node.closest ? node.closest('form') : null;
    const variantInput = (form && form.querySelector('input[name="id"]')) || document.querySelector('form.js-product-form input[name="id"]');
    const qtyInput = (form && form.querySelector('input[name="quantity"]')) || document.querySelector('form.js-product-form input[name="quantity"]');

    return {
      product_id: product ? product.id : null,
      product_title: product ? product.title : null,
      variant_id: variantInput && variantInput.value ? Number(variantInput.value) : null,
      quantity: qtyInput && qtyInput.value ? Number(qtyInput.value) : 1
    };
  }

  // 判断交互来源场景，便于后续分析不同入口的转化效果。
  function getInteractionSource(element) {
    if (!element || !element.closest) return 'pdp';
    if (element.closest('.js-cart-drawer')) return 'drawer';
    if (element.closest('#cartform') || element.closest('.template-cart')) return 'cart';
    if (element.closest('.quickbuy-content') || element.closest('[data-quickbuy]')) return 'quickbuy';
    if (element.closest('.additional-checkout-buttons')) return 'checkout';
    return 'pdp';
  }

  // 更新并返回某商品某类型选项的切换次数（color/size）。
  function updateVariantSwitchCounter(productId, type) {
    if (!productId || !type) return 0;
    const key = `${productId}_${type}`;
    const prev = variantSwitchCounters.get(key) || 0;
    const next = prev + 1;
    variantSwitchCounters.set(key, next);
    return next;
  }

  // 页面初始化事件：
  // product 页发送 pdp_view，并在 180s 后发送停留事件；
  // cart 页发送 cart_view。
  function trackInitialPageViews() {
    const pageType = getPageType();
    if (pageType === 'product') {
      const ctx = getCurrentProductContext(document.body);
      const revisitKey = `pdp_revisit:${window.location.pathname}`;
      const revisitCount = Number(window.localStorage.getItem(revisitKey) || '0') + 1;
      window.localStorage.setItem(revisitKey, String(revisitCount));
      pushTrackingEvent('pdp_view', Object.assign({}, ctx, {
        interaction_source: 'pdp',
        reason_bucket_hint: 'choice',
        pdp_revisit_count: revisitCount
      }));

      window.setTimeout(function () {
        pushTrackingEvent('pdp_engaged_180s', Object.assign({}, ctx, {
          interaction_source: 'pdp',
          reason_bucket_hint: 'choice',
          engagement_seconds: 180
        }));
      }, 180000);
    }

    if (pageType === 'cart') {
      pushTrackingEvent('cart_view', {
        interaction_source: 'cart',
        reason_bucket_hint: 'value'
      });
    }
  }

  // 从变体选择器中提取选项名（如 color / size）。
  function getOptionNamesFromForm(form) {
    if (!form) return [];
    const selectors = form.querySelectorAll('variant-picker .option-selector');
    const optionNames = [];
    selectors.forEach((selector) => {
      const labelEl = selector.querySelector('legend, .label');
      const labelText = labelEl ? labelEl.textContent || '' : '';
      optionNames.push(labelText.trim().toLowerCase());
    });
    return optionNames;
  }

  // 按选项名归类类型，当前仅识别 color 和 size。
  function classifyOption(name) {
    if (!name) return null;
    if (/(color|colour)/i.test(name)) return 'color';
    if (/(size|灏虹爜|灏哄)/i.test(name)) return 'size';
    return null;
  }

  // 绑定变体切换监听（on:variant:change）。
  // 仅在选项值发生变化时上报，避免重复渲染造成噪声。
  function bindVariantTracking() {
    document.addEventListener('on:variant:change', function (evt) {
      const detail = evt.detail || {};
      const variant = detail.variant || {};
      const form = detail.form || (evt.target && evt.target.closest ? evt.target.closest('form') : null);
      const selectedOptions = detail.selectedOptions || [];
      const optionNames = getOptionNamesFromForm(form);
      const ctx = getCurrentProductContext(form || document.body);
      const productId = ctx.product_id || (form && form.dataset ? form.dataset.productId : null) || null;

      const stateKey = productId || 'global';
      const prevState = interactionTimers.get(stateKey) || [];
      interactionTimers.set(stateKey, selectedOptions.slice());

      selectedOptions.forEach((value, idx) => {
        if (value === null || typeof value === 'undefined') return;
        const prev = prevState[idx];
        if (prev === value) return;

        const optionType = classifyOption(optionNames[idx] || '');
        if (optionType === 'color') {
          // 颜色切换事件，记录切换次数与当前选择值。
          const switchCount = updateVariantSwitchCounter(productId, 'color');
          pushTrackingEvent('pdp_variant_change_color', Object.assign({}, ctx, {
            variant_id: variant.id || ctx.variant_id || null,
            sku: variant.sku || null,
            option_name: optionNames[idx] || null,
            option_value: value,
            switch_count_color: switchCount,
            interaction_source: getInteractionSource(form),
            reason_bucket_hint: 'choice'
          }));
        } else if (optionType === 'size') {
          // 尺码切换事件，记录切换次数与当前选择值。
          const switchCount = updateVariantSwitchCounter(productId, 'size');
          pushTrackingEvent('pdp_variant_change_size', Object.assign({}, ctx, {
            variant_id: variant.id || ctx.variant_id || null,
            sku: variant.sku || null,
            option_name: optionNames[idx] || null,
            option_value: value,
            switch_count_size: switchCount,
            interaction_source: getInteractionSource(form),
            reason_bucket_hint: 'choice'
          }));
        }
      });
    });
  }

  // 绑定购物车相关监听：
  // on:cart:add -> add_to_cart
  // on:cart-drawer:after-open -> cart_view
  function bindCartTracking() {
    document.addEventListener('on:cart:add', function (evt) {
      const ctx = getCurrentProductContext(evt.target);
      const variantId = evt.detail && evt.detail.variantId ? Number(evt.detail.variantId) : ctx.variant_id;
      pushTrackingEvent('add_to_cart', Object.assign({}, ctx, {
        variant_id: variantId,
        interaction_source: getInteractionSource(evt.target),
        reason_bucket_hint: 'value'
      }));
    });

    document.addEventListener('on:cart-drawer:after-open', function () {
      pushTrackingEvent('cart_view', {
        interaction_source: 'drawer',
        reason_bucket_hint: 'value'
      });
    });
  }

  // 绑定结账入口相关监听（使用捕获阶段，尽早获取点击）。
  // 包含去结账、Buy Now、更多支付方式三个入口。
  function bindCheckoutTracking() {
    document.addEventListener('click', function (evt) {
      const target = evt.target && evt.target.closest ? evt.target.closest('#cartform [name="checkout"], .checkout-buttons [name="checkout"], .checkout-buttons a[href*="/checkout"]') : null;
      if (!target) return;
      pushTrackingEvent('begin_checkout_click', {
        interaction_source: getInteractionSource(target),
        reason_bucket_hint: 'value'
      });
    }, true);

    document.addEventListener('click', function (evt) {
      const target = evt.target && evt.target.closest ? evt.target.closest('.shopify-payment-button button, .shopify-payment-button__button') : null;
      if (!target) return;
      const ctx = getCurrentProductContext(target);
      pushTrackingEvent('buy_now_click', Object.assign({}, ctx, {
        interaction_source: getInteractionSource(target),
        reason_bucket_hint: 'value'
      }));
    }, true);

    document.addEventListener('click', function (evt) {
      const target = evt.target && evt.target.closest ? evt.target.closest('.additional-checkout-buttons input, .additional-checkout-buttons button, .additional-checkout-buttons a') : null;
      if (!target) return;
      pushTrackingEvent('payment_option_more_click', {
        interaction_source: 'checkout',
        reason_bucket_hint: 'value'
      });
    }, true);
  }

  // 绑定内容交互监听：
  // 尺码表、放大图、配送/退换信息、优惠码、评论曝光等。
  function bindContentTracking() {
    // 监听商品页折叠区标题点击（含“+”按钮），记录用户的展开/收起意图。
    document.addEventListener('click', function (evt) {
      const summary = evt.target && evt.target.closest ? evt.target.closest('details.disclosure > summary.disclosure__title') : null;
      if (!summary) return;
      const disclosure = summary.closest('details.disclosure');
      if (!disclosure) return;

      const title = (summary.textContent || '').trim().toLowerCase();
      const willOpen = !disclosure.open;
      const ctx = getCurrentProductContext(summary);
      pushTrackingEvent('pdp_accordion_toggle_click', Object.assign({}, ctx, {
        interaction_source: getInteractionSource(summary),
        reason_bucket_hint: 'choice',
        content_title: title || null,
        option_value: willOpen ? 'open' : 'close'
      }));
    }, true);

    // 监听 Fabric / Description 中 “See more / See less” 按钮，记录展开/收起动作。
    document.addEventListener('click', function (evt) {
      const seeMoreBtn = evt.target && evt.target.closest ? evt.target.closest('.see-more-btn') : null;
      if (!seeMoreBtn) return;

      const disclosure = seeMoreBtn.closest('details.disclosure');
      const summary = disclosure ? disclosure.querySelector('summary.disclosure__title') : null;
      const title = summary ? (summary.textContent || '').trim().toLowerCase() : null;
      const textBlock = seeMoreBtn.previousElementSibling;
      const isCollapsed = !!(textBlock && (textBlock.classList.contains('clamp-3') || textBlock.classList.contains('clamp-content')));
      const ctx = getCurrentProductContext(seeMoreBtn);
      pushTrackingEvent('pdp_inline_content_toggle_click', Object.assign({}, ctx, {
        interaction_source: getInteractionSource(seeMoreBtn),
        reason_bucket_hint: 'choice',
        content_title: title,
        option_value: isCollapsed ? 'expand' : 'collapse'
      }));
    }, true);

    document.addEventListener('click', function (evt) {
      const sizeChart = evt.target && evt.target.closest ? evt.target.closest('.size-chart-link, modal-opener[data-modal*="size-chart"]') : null;
      if (sizeChart) {
        const ctx = getCurrentProductContext(sizeChart);
        pushTrackingEvent('pdp_size_chart_open', Object.assign({}, ctx, {
          interaction_source: getInteractionSource(sizeChart),
          reason_bucket_hint: 'fit'
        }));
      }
    }, true);

    document.addEventListener('click', function (evt) {
      const zoomLink = evt.target && evt.target.closest ? evt.target.closest('a.show-gallery') : null;
      if (!zoomLink) return;
      const ctx = getCurrentProductContext(zoomLink);
      pushTrackingEvent('pdp_image_zoom', Object.assign({}, ctx, {
        interaction_source: getInteractionSource(zoomLink),
        reason_bucket_hint: 'choice'
      }));
    }, true);

    document.addEventListener('click', function (evt) {
      const shippingToggle = evt.target && evt.target.closest ? evt.target.closest('[data-toggle-target*="shipping-calculator"]') : null;
      if (!shippingToggle) return;
      pushTrackingEvent('pdp_shipping_info_view', {
        interaction_source: getInteractionSource(shippingToggle),
        reason_bucket_hint: 'logistics'
      });
    }, true);

    document.addEventListener('click', function (evt) {
      const couponControl = evt.target && evt.target.closest
        ? evt.target.closest('button[name*="discount"], [name="apply_discount"], form[action*="discount"] button')
        : null;
      if (!couponControl) return;
      pushTrackingEvent('coupon_attempt', {
        interaction_source: getInteractionSource(couponControl),
        reason_bucket_hint: 'value'
      });
    }, true);

    // 监听详情折叠区打开动作，根据标题关键词分流到不同事件。
    document.addEventListener('toggle', function (evt) {
      const disclosure = evt.target;
      if (!disclosure || !disclosure.matches || !disclosure.matches('details.disclosure') || !disclosure.open) return;
      const summary = disclosure.querySelector('summary');
      const title = summary ? (summary.textContent || '').trim().toLowerCase() : '';
      const ctx = getCurrentProductContext(disclosure);

      if (/(fit|size)/i.test(title)) {
        pushTrackingEvent('pdp_fit_content_view', Object.assign({}, ctx, {
          interaction_source: getInteractionSource(disclosure),
          reason_bucket_hint: 'fit',
          content_title: title
        }));
      } else if (/(review|rating)/i.test(title)) {
        pushTrackingEvent('pdp_review_view', Object.assign({}, ctx, {
          interaction_source: getInteractionSource(disclosure),
          reason_bucket_hint: 'trust',
          content_title: title
        }));
      } else if (/(shipping|delivery|eta)/i.test(title)) {
        pushTrackingEvent('pdp_shipping_info_view', Object.assign({}, ctx, {
          interaction_source: getInteractionSource(disclosure),
          reason_bucket_hint: 'logistics',
          content_title: title
        }));
      }
    }, true);

    // 通过链接 href 关键词兜底识别 shipping / return / refund 相关点击。
    document.addEventListener('click', function (evt) {
      const policyLink = evt.target && evt.target.closest ? evt.target.closest('a[href*="shipping"], a[href*="return"], a[href*="refund"], a[href*="policy"]') : null;
      if (!policyLink) return;
      const href = (policyLink.getAttribute('href') || '').toLowerCase();
      if (href.indexOf('shipping') > -1) {
        pushTrackingEvent('pdp_shipping_info_view', {
          interaction_source: getInteractionSource(policyLink),
          reason_bucket_hint: 'logistics',
          link_href: href
        });
      }
    }, true);

    // 评论模块进入可视区域后上报一次，避免“未阅读即上报”。
    const reviewsBlock = document.querySelector('.theme-product-reviews, .jdgm-widget, .spr-badge');
    if (reviewsBlock && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const ctx = getCurrentProductContext(entry.target);
          pushTrackingEvent('pdp_review_view', Object.assign({}, ctx, {
            interaction_source: getInteractionSource(entry.target),
            reason_bucket_hint: 'trust'
          }));
          observer.disconnect();
        });
      }, { threshold: 0.4 });
      io.observe(reviewsBlock);
    }
  }

  // 初始化入口：按模块挂载全部监听器。
  function init() {
    trackInitialPageViews();
    bindVariantTracking();
    bindCartTracking();
    bindCheckoutTracking();
    bindContentTracking();
  }

  // 确保 DOM 可用后再初始化，避免节点未渲染导致监听上下文缺失。
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
